'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { DynamicIcon } from 'lucide-react/dynamic';
import { useRef, useState } from 'react';
import { serviceCategories } from '@/content/navigation';

export default function AllServicesByCategory() {
    return (
        <section className="bg-gradient-to-b from-primary/10 to-secondary/10 mt-[-80px]">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="mb-12 md:mb-16 text-center">
                    <h1 className="fs-72 mt-18 mb-6 max-w-4xl mx-auto">
                        Publishing Solutions That Amplify Your Story
                    </h1>
                    <p className="max-w-3xl section-description mx-auto text-xl">
                        Strategic services for modern authors and brands
                    </p>
                </div>

                <div className="space-y-16 md:space-y-20">
                    {serviceCategories.map((cat) => (
                        <div key={cat.url} className="scroll-mt-20" id={cat.url}>
                            {/* ✅ Category Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 md:mb-10">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/60 border border-white/30 shadow-sm">
                                        {cat.icon ? (
                                            <DynamicIcon
                                                name={cat.icon}
                                                className="w-6 h-6 text-primary"
                                            />
                                        ) : null}
                                    </div>
                                    <div>
                                        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                            {cat.title} Development
                                        </h2>
                                        {cat.description ? (
                                            <p className="text-muted-foreground mt-2">
                                                {cat.description}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>

                                {/* {cat.url ? (
                                    <Link
                                        href={`/${cat.url}`}
                                        className="text-sm font-medium text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
                                    >
                                        View all {cat.title.toLowerCase()} services
                                        <DynamicIcon name="arrow-right" className="w-4 h-4" />
                                    </Link>
                                ) : null} */}
                            </div>

                            {/* ✅ Platforms-style cards grid */}
                            <ServicesGrid
                                items={cat.links.map(([label, slug]) => ({
                                    label,
                                    slug,
                                    icon: cat.icon,
                                }))}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ServicesGrid({ items }) {
    const containerRef = useRef(null);
    const [overlayPos, setOverlayPos] = useState({
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        visible: false,
    });
    const [hoveredIndex, setHoveredIndex] = useState(null);

    const handleEnter = (e, index) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const item = e.currentTarget.getBoundingClientRect();

        setHoveredIndex(index);
        setOverlayPos({
            x: item.left - rect.left,
            y: item.top - rect.bottom,
            w: item.width,
            h: item.height,
            visible: true,
        });
    };

    const handleLeave = () => {
        setHoveredIndex(null);
        setOverlayPos((prev) => ({ ...prev, visible: false }));
    };

    return (
        <div ref={containerRef} className="relative">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
                {items.map((item, i) => (
                    <Link
                        key={item.slug}
                        href={`${item.slug}`}
                        onMouseEnter={(e) => handleEnter(e, i)}
                        onMouseLeave={handleLeave}
                        className="relative p-8 rounded-4xl border border-white/10 cursor-pointer group shadow-lg min-h-[240px] flex flex-col justify-center outline-none no-underline"
                    >
                        {/* ✅ White Background (visible normally, hidden on hover) */}
                        <motion.div
                            className="absolute inset-0 bg-white rounded-4xl z-0"
                            initial={{ opacity: 1 }}
                            animate={{ opacity: hoveredIndex === i ? 0 : 1 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                        />

                        {/* ✅ Content */}
                        <div className="z-20 relative">
                            {item.icon ? (
                                <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-lg bg-black/5 group-hover:bg-white">
                                    <DynamicIcon
                                        name={item.icon}
                                        className="transition-all duration-300 text-primary w-6 h-6"
                                    />
                                </div>
                            ) : null}

                            <h3 className="text-xl font-semibold mb-2 text-foreground group-hover:text-white transition-colors">
                                {item.label}
                            </h3>

                            <p className="text-base text-foreground/80 group-hover:text-white/90 leading-relaxed mb-4">
                                Professional {item.label.toLowerCase()} services to elevate your
                                project.
                            </p>

                            <div className="inline-flex items-center text-primary group-hover:text-white font-medium transition-colors">
                                Explore service
                                <DynamicIcon
                                    name="chevron-right"
                                    className="w-5 h-5 ml-1 transform group-hover:translate-x-1 transition-transform"
                                />
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            {/* ✅ Single persistent overlay like PlatformsSection */}
            <motion.div
                className="absolute rounded-4xl common-gradient-primary z-0"
                initial={false}
                animate={{
                    x: overlayPos.x,
                    y: overlayPos.y,
                    width: overlayPos.w,
                    height: overlayPos.h,
                    opacity: overlayPos.visible ? 1 : 0,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
        </div>
    );
}
